import { useEffect, useState } from "react";
import { FileUp, Save, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProviderTimeOffPanel } from "@/components/providers/ProviderTimeOffPanel";

const NAME_EDIT_ROLES = ["system_administrator", "hr"];
const TIME_OFF_ROLES = ["system_administrator", "hr", "operations", "staffing_coordinator"];
const emptyProfile = {
  first_name: "",
  last_name: "",
  discipline: "",
  credentials: "",
  license_state: "",
  license_number: "",
  engagement_type: "",
};
const acceptedCvTypes =
  "application/pdf,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export const ProviderProfileEditor = ({ provider, onClose, onUpdated }) => {
  const { user } = useAuth();
  const canEditName = NAME_EDIT_ROLES.includes(user?.role);
  const [profile, setProfile] = useState(emptyProfile);
  const [resume, setResume] = useState(null);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!provider) {
      return;
    }
    setProfile({
      first_name: provider.first_name || "",
      last_name: provider.last_name || "",
      discipline: provider.discipline || "",
      credentials: provider.credentials || "",
      license_state: provider.license_state || "",
      license_number: provider.license_number || "",
      engagement_type: ["1099", "W-2"].includes(provider.engagement_type) ? provider.engagement_type : provider.engagement_type === "W2" ? "W-2" : "",
    });
    setResume(null);
    setError("");
  }, [provider]);

  if (!provider) {
    return null;
  }

  async function saveProfile(event) {
    event.preventDefault();
    setIsSaving(true);
    try {
      const response = await api.put(`/providers/${provider.id}/profile`, canEditName ? profile : { ...profile, first_name: undefined, last_name: undefined });
      onUpdated(response.data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsSaving(false);
    }
  }

  async function uploadResume() {
    if (!resume) {
      setError("Choose a PDF or DOCX CV first.");
      return;
    }
    setIsSaving(true);
    try {
      const data = new FormData();
      data.append("resume", resume);
      const response = await api.post(`/providers/${provider.id}/resume`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpdated(response.data);
      setResume(null);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="provider-profile-editor" data-testid="provider-profile-editor">
      <div className="section-heading">
        <div>
          <h2>Complete provider profile</h2>
          <p>
            {provider.first_name} {provider.last_name} · internal TAG records only
          </p>
        </div>
        <button
          aria-label="Close provider profile editor"
          className="projection-icon-button"
          data-testid="close-provider-profile-editor-button"
          type="button"
          onClick={onClose}
        >
          <X size={16} />
        </button>
      </div>
      {error && (
        <p className="form-message error-message" data-testid="provider-profile-editor-error">
          {error}
        </p>
      )}
      <form
        className="provider-profile-form"
        data-testid="provider-profile-form"
        onSubmit={saveProfile}
      >
        {canEditName && (
          <>
            <label>
              First name
              <input
                data-testid="provider-profile-first-name-input"
                value={profile.first_name}
                onChange={(event) => setProfile({ ...profile, first_name: event.target.value })}
              />
            </label>
            <label>
              Last name
              <input
                data-testid="provider-profile-last-name-input"
                value={profile.last_name}
                onChange={(event) => setProfile({ ...profile, last_name: event.target.value })}
              />
            </label>
          </>
        )}
        <label>
          Discipline / specialty
          <input
            data-testid="provider-profile-discipline-input"
            value={profile.discipline}
            onChange={(event) => setProfile({ ...profile, discipline: event.target.value })}
          />
        </label>
        <label>
          Engagement type
          <select
            data-testid="provider-profile-engagement-select"
            value={profile.engagement_type}
            onChange={(event) => setProfile({ ...profile, engagement_type: event.target.value })}
          >
            <option value="">Not set (hides 1099 invoicing)</option>
            <option value="1099">1099 contractor</option>
            <option value="W-2">W-2 employee</option>
          </select>
        </label>
        <label>
          Credentials
          <input
            data-testid="provider-profile-credentials-input"
            value={profile.credentials}
            onChange={(event) => setProfile({ ...profile, credentials: event.target.value })}
          />
        </label>
        <label>
          License state
          <input
            data-testid="provider-profile-license-state-input"
            value={profile.license_state}
            onChange={(event) => setProfile({ ...profile, license_state: event.target.value })}
          />
        </label>
        <label>
          License number
          <input
            data-testid="provider-profile-license-number-input"
            value={profile.license_number}
            onChange={(event) => setProfile({ ...profile, license_number: event.target.value })}
          />
        </label>
        <button
          className="button button-primary"
          data-testid="save-provider-profile-button"
          disabled={isSaving}
        >
          <Save size={16} /> Save profile
        </button>
      </form>
      <div className="provider-cv-upload">
        <div>
          <strong>CV or résumé</strong>
          <span data-testid="provider-cv-status">
            {provider.resume_uploaded ? provider.resume_filename : "Not uploaded"}
          </span>
        </div>
        <input
          accept={acceptedCvTypes}
          data-testid="provider-cv-upload-input"
          type="file"
          onChange={(event) => setResume(event.target.files?.[0] || null)}
        />
        <button
          className="button button-secondary"
          data-testid="upload-provider-cv-button"
          disabled={!resume || isSaving}
          type="button"
          onClick={uploadResume}
        >
          <FileUp size={16} /> Upload CV
        </button>
      </div>
      {TIME_OFF_ROLES.includes(user?.role) && provider.approval_status === "Approved" && <ProviderTimeOffPanel providerId={provider.id} />}
    </section>
  );
};
